import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Spinner } from 'react-bootstrap';
import {
    FaNotesMedical,
    FaUsers,
    FaClock,
    FaCalendarCheck,
    FaRegCalendarTimes,
    FaUserAltSlash
} from 'react-icons/fa';
import { collection, query, where, onSnapshot, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { useOutletContext, useNavigate } from 'react-router-dom';

const DoctorDashboard = () => {
    const navigate = useNavigate();
    const { userData } = useOutletContext();

    const [scheduled, setScheduled] = useState([]);
    const [pending, setPending] = useState([]);
    const [completed, setCompleted] = useState([]);
    const [loading, setLoading] = useState(true);
    const [now, setNow] = useState(new Date());

    useEffect(() => {
        if (!userData.uid) return;

        const unsub = onSnapshot(
            query(collection(db, 'appointments'), where('doctorId', '==', userData.uid)),
            async (snap) => {
                const apps = [];
                const nowTs = new Date();
                for (const d of snap.docs) {
                    const data = d.data();
                    const dt = new Date(`${data.appointmentDate}T${data.appointmentTime}`);
                    if (dt < nowTs && ['Scheduled', 'Confirmed'].includes(data.status)) {
                        await updateDoc(doc(db, 'appointments', d.id), { status: 'Pending' });
                        data.status = 'Pending';
                    }
                    apps.push({ id: d.id, ...data });
                }
                setScheduled(apps.filter(a => ['Scheduled', 'Confirmed'].includes(a.status)));
                setPending(apps.filter(a => a.status === 'Pending'));
                setCompleted(apps.filter(a => a.status === 'Completed'));
                setLoading(false);
            }
        );

        return () => unsub();
    }, [userData]);

    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    const getStatusBadge = status => ({
        Completed: 'success',
        Cancelled: 'danger',
        Pending: 'warning',
        Scheduled: 'primary',
        Confirmed: 'primary',
    }[status] || 'secondary');

    return (
        <div className="container-fluid py-4">
            {/* Welcome Banner */}
            <div className="welcome-banner mb-4 shadow-sm" style={{
                background: 'linear-gradient(135deg, #4318FF 0%, #868CFF 100%)',
                borderRadius: '20px',
                padding: '30px',
                color: '#fff',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <div style={{ position: 'relative', zIndex: 2 }}>
                    <h2 className="fw-bold mb-2">
                        👋 Welcome, Dr. {userData?.name || 'Guest'}!
                    </h2>
                    <p className="mb-0" style={{ opacity: 0.9 }}>
                        Manage your practice efficiently with your personal dashboard.
                    </p>
                    <p className="mb-0 mt-2" style={{ opacity: 0.8, fontSize: '0.9rem' }}>
                        Here’s a quick overview of your appointments, patients, and schedules. 
                        <strong> Note:</strong> Ensure your contact details are up-to-date for appointment reminders.
                    </p>
                </div>
                {/* Decorative circles */}
                <div style={{
                    position: 'absolute', top: '-50px', right: '-20px', width: '150px', height: '150px',
                    background: 'rgba(255,255,255,0.1)', borderRadius: '50%'
                }} />
                <div style={{
                    position: 'absolute', bottom: '-40px', right: '80px', width: '100px', height: '100px',
                    background: 'rgba(255,255,255,0.1)', borderRadius: '50%'
                }} />
            </div>

            {/* Top Stat Cards */}
            <Row className="mb-4 gy-4">
                <Col md={3}>
                    <Card className="shadow-sm border-0 rounded-3 h-100 stat-card" style={{ transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}>
                        <Card.Body className="d-flex align-items-center">
                            <div className="rounded-circle p-3 bg-primary bg-opacity-10 me-3 text-primary">
                                <FaNotesMedical size={28} />
                            </div>
                            <div>
                                <h6 className="text-muted mb-1">Upcoming</h6>
                                <h3 className="mb-0 fw-bold">{loading ? <Spinner animation="border" size="sm" /> : scheduled.length}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="shadow-sm border-0 rounded-3 h-100 stat-card" style={{ transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}>
                        <Card.Body className="d-flex align-items-center">
                            <div className="rounded-circle p-3 bg-warning bg-opacity-10 me-3 text-warning">
                                <FaClock size={28} />
                            </div>
                            <div>
                                <h6 className="text-muted mb-1">Pending</h6>
                                <h3 className="mb-0 fw-bold">{loading ? <Spinner animation="border" size="sm" /> : pending.length}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="shadow-sm border-0 rounded-3 h-100 stat-card" style={{ transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}>
                        <Card.Body className="d-flex align-items-center">
                            <div className="rounded-circle p-3 bg-success bg-opacity-10 me-3 text-success">
                                <FaUsers size={28} />
                            </div>
                            <div>
                                <h6 className="text-muted mb-1">Completed</h6>
                                <h3 className="mb-0 fw-bold">{loading ? <Spinner animation="border" size="sm" /> : completed.length}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="shadow-sm border-0 rounded-3 h-100 stat-card" style={{ transition: 'transform 0.2s ease, box-shadow 0.2s ease', background: 'linear-gradient(135deg, #111C44 0%, #1A2753 100%)', color: '#fff' }}>
                        <Card.Body className="d-flex align-items-center">
                            <div className="rounded-circle p-3 bg-light bg-opacity-10 me-3 text-white">
                                <FaCalendarCheck size={28} />
                            </div>
                            <div>
                                <h6 className="mb-1 text-white" style={{ opacity: 0.8 }}>{now.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' })}</h6>
                                <h4 className="mb-0 fw-bold text-white">{now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</h4>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mt-4 gy-4">
                {[{ title: "Upcoming", list: scheduled },
                { title: "Pending", list: pending }].map(({ title, list }) => (
                    <Col md={6} key={title}>
                        <Card className="shadow-sm border-0 rounded-3 h-100">
                            <Card.Header className="bg-white border-0 pt-4 pb-0">
                                <h5 className="mb-0 text-primary fw-bold">{title} Appointments</h5>
                            </Card.Header>
                            <Card.Body>
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Patient</th><th>Date</th><th>Time</th><th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {list.slice(0, 5).map(a => (
                                                <tr key={a.id}>
                                                    <td>{a.fullName}</td>
                                                    <td>{a.appointmentDate}</td>
                                                    <td>{a.appointmentTime}</td>
                                                    <td><span className={`badge bg-${getStatusBadge(a.status)} text-white px-2 py-1`}>{a.status}</span></td>
                                                </tr>
                                            ))}
                                            {list.length === 0 && (
                                                <tr>
                                                    <td colSpan="4" className="text-center text-muted py-4">
                                                        <FaRegCalendarTimes size={24} className="mb-2 text-secondary d-block mx-auto" />
                                                        No {title} appointments.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </Card.Body>
                            <Card.Footer className="bg-white border-0 text-center pb-3">
                                <Button variant="outline-primary" className="rounded-pill px-4" size="sm"
                                    onClick={() => navigate(`/doctor/appointments`)}>
                                    View All
                                </Button>
                            </Card.Footer>
                        </Card>
                    </Col>
                ))}
            </Row>

            <Row className="mt-4 gy-4">
                <Col>
                    <Card className="shadow-sm border-0 rounded-3">
                        <Card.Header className="bg-white border-0 pt-4 pb-0">
                            <h5 className="mb-0 text-primary fw-bold">Recent Patients</h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Patient Code</th><th>Name</th><th>Age</th><th>Last Visit</th><th>Contact</th><th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {completed.slice(0, 5).map((a, idx) => (
                                            <tr key={idx}>
                                                <td><span className="fw-medium">{a.patientCode || 'N/A'}</span></td>
                                                <td>{a.fullName}</td>
                                                <td>{a.age || 'N/A'}</td>
                                                <td>{a.appointmentDate}</td>
                                                <td>{a.phone || 'N/A'}</td>
                                                <td><span className={`badge bg-${getStatusBadge(a.status)} text-white px-2 py-1`}>{a.status}</span></td>
                                            </tr>
                                        ))}
                                        {completed.length === 0 && (
                                            <tr>
                                                <td colSpan="6" className="text-center text-muted py-5">
                                                    <FaUserAltSlash size={30} className="mb-3 text-secondary d-block mx-auto" />
                                                    No patients yet.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Card.Body>
                        <Card.Footer className="bg-white border-0 text-center pb-4">
                            <Button variant="outline-primary" className="rounded-pill px-4" size="sm"
                                onClick={() => navigate(`/doctor/patients`)}>
                                View All Patients
                            </Button>
                        </Card.Footer>
                    </Card>
                </Col>
            </Row>

            <Row className="mt-4 mb-4">
                <Col md={12}>
                    <Card className="shadow-sm border-0 rounded-3 text-center p-4" style={{ background: 'linear-gradient(135deg, #f4f7fe 0%, #ffffff 100%)' }}>
                        <div className="text-center mb-3">
                            <FaCalendarCheck size={40} className="text-primary mb-3" />
                            <h4 className="fw-bold text-dark">Manage Your Schedule</h4>
                        </div>
                        <p className="text-muted mb-4 mx-auto" style={{ maxWidth: '600px' }}>
                            Keep your schedule organized and up-to-date. You can add, edit, or remove your availability slots.
                            Ensure your patients can book appointments at your convenience.
                        </p>
                        <div>
                            <Button
                                variant="primary"
                                className="rounded-pill px-4 shadow-sm"
                                onClick={() => navigate('/doctor/schedules')}>
                                Manage Schedules
                            </Button>
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default DoctorDashboard;
